import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save, Check, AlertTriangle } from "lucide-react";

const EMPTY = {
  full_name: "", birth_date: "", city: "", state: "", notes: "",
  phone: "", whatsapp: "", email: "", preferred_contact: "whatsapp", can_contact: false,
  church: "", ministry: "", first_visit_date: "", is_first_timer: false,
  parent_consent: false, responsible_name: "", responsible_contact: "",
};

const STEPS = ["Dados pessoais", "Contato", "Igreja", "Acompanhamento", "Revisão"];
const DRAFT_KEY = "ci_youth_draft";

// Keep Field outside AdminYouthForm so React does not remount inputs on every keystroke.
function Field({ label, children, required }) {
  return (
    <div>
      <label className="label block mb-2">
        {label}{required && " *"}
      </label>
      {children}
    </div>
  );
}

export default function AdminYouthForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [dupes, setDupes] = useState([]);
  const [saved, setSaved] = useState(null);
  const saveTimer = useRef(null);

  // Load existing
  useEffect(() => {
    if (id && id !== "novo") {
      api.get(`/youth/${id}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => toast.error("Erro ao carregar"));
    } else {
      // restore draft
      try {
        const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
        if (d) setForm({ ...EMPTY, ...d });
      } catch {}
    }
  }, [id]);

  // Auto-save draft (only for new)
  useEffect(() => {
    if (id && id !== "novo") return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [form, id]);

  // Duplicate check on name/phone change
  useEffect(() => {
    if (id && id !== "novo") return;
    const q = (form.full_name || "").trim() || (form.whatsapp || "").trim() || (form.phone || "").trim();
    if (q.length < 3) { setDupes([]); return; }
    const t = setTimeout(() => {
      api.get(`/youth/search?q=${encodeURIComponent(q)}`).then((r) => setDupes(r.data || [])).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.full_name, form.whatsapp, form.phone, id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const age = form.birth_date ? Math.max(0, Math.floor((Date.now() - new Date(form.birth_date).getTime()) / 3.15576e10)) : null;
  const isMinor = age != null && age < 18;

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error("Nome completo é obrigatório");
      setStep(0); return;
    }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.full_name = form.full_name.trim();
      const r = id && id !== "novo" ? await api.put(`/youth/${id}`, payload) : await api.post("/youth", payload);
      localStorage.removeItem(DRAFT_KEY);
      setSaved(r.data);
      toast.success("Cadastro salvo!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  if (saved) {
    return (
      <div className="max-w-xl mx-auto text-center space-y-5">
        <div className="ci-card p-8 animate-fade-up">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 border border-blue-500/40 mx-auto flex items-center justify-center mb-4">
            <Check size={32} className="text-blue-400" />
          </div>
          <h1 className="font-display text-2xl font-black">Cadastro salvo</h1>
          <p className="text-slate-300 mt-2">{saved.full_name} foi adicionado ao sistema.</p>
          {!saved.is_complete && (
            <div className="mt-4 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 inline-flex items-center gap-2">
              <AlertTriangle size={14}/> Cadastro incompleto — pode ser completado depois
            </div>
          )}
          <div className="mt-8 grid gap-3">
            <button onClick={() => nav(`/admin/presenca?youth=${saved.id}`)} className="btn-primary w-full">Registrar presença</button>
            <button onClick={() => nav(`/admin/jovens/${saved.id}`)} className="btn-ghost w-full">Ver ficha</button>
            <button onClick={() => { setSaved(null); setForm(EMPTY); setStep(0); }} className="btn-ghost w-full">Cadastrar outro jovem</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button onClick={() => nav("/admin/jovens")} className="text-xs text-slate-400 hover:text-blue-400 inline-flex items-center gap-1"><ArrowLeft size={12}/> Voltar à lista</button>
      <div>
        <div className="label">Gestão de Jovens</div>
        <h1 className="font-display text-3xl font-black mt-2">{id && id !== "novo" ? "Editar ficha" : "Novo cadastro"}</h1>
        <div className="mt-4">
          <div className="flex items-center gap-2 text-xs mb-2">
            <span className="label">Etapa {step + 1} de {STEPS.length}</span>
            <span className="text-slate-400">· {STEPS[step]}</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="ci-card p-5 sm:p-7 space-y-5" data-testid="youth-form">
        {step === 0 && (
          <>
            <Field label="Nome completo" required>
              <input data-testid="youth-fullname" required value={form.full_name} onChange={(e)=>set("full_name", e.target.value)} className="ci-input" autoFocus />
            </Field>
            {dupes.length > 0 && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3">
                <div className="text-xs font-bold text-amber-300 flex items-center gap-2"><AlertTriangle size={14}/> Possíveis cadastros existentes</div>
                <div className="mt-2 space-y-2">
                  {dupes.map((d) => (
                    <button key={d.id} type="button" onClick={() => nav(`/admin/jovens/${d.id}`)}
                            className="w-full text-left text-sm bg-black/40 hover:bg-black/60 border border-white/10 rounded-lg px-3 py-2">
                      <div className="font-medium text-white">{d.full_name}</div>
                      <div className="text-xs text-slate-400">{d.whatsapp || d.phone || "—"} · {d.church || "—"}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Data de nascimento">
                <input data-testid="youth-birthdate" type="date" value={form.birth_date?.slice(0,10) || ""} onChange={(e)=>set("birth_date", e.target.value)} className="ci-input" />
                {age != null && <div className="text-xs text-slate-500 mt-1">{age} anos</div>}
              </Field>
              <Field label="Cidade / Estado">
                <div className="grid grid-cols-[1fr_80px] gap-2">
                  <input placeholder="Cidade" value={form.city} onChange={(e)=>set("city", e.target.value)} className="ci-input" />
                  <input placeholder="UF" maxLength={2} value={form.state} onChange={(e)=>set("state", e.target.value.toUpperCase())} className="ci-input" />
                </div>
              </Field>
            </div>
            <Field label="Observações administrativas">
              <textarea rows={3} value={form.notes} onChange={(e)=>set("notes", e.target.value)} className="ci-input resize-none" />
            </Field>
          </>
        )}

        {step === 1 && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="WhatsApp">
                <input data-testid="youth-whatsapp" placeholder="(11) 99999-9999" value={form.whatsapp} onChange={(e)=>set("whatsapp", e.target.value)} className="ci-input" />
              </Field>
              <Field label="Telefone">
                <input placeholder="(11) 3333-4444" value={form.phone} onChange={(e)=>set("phone", e.target.value)} className="ci-input" />
              </Field>
            </div>
            <Field label="E-mail">
              <input type="email" value={form.email} onChange={(e)=>set("email", e.target.value)} className="ci-input" />
            </Field>
            <Field label="Preferência de contato">
              <select value={form.preferred_contact || "whatsapp"} onChange={(e)=>set("preferred_contact", e.target.value)} className="ci-input">
                <option value="whatsapp">WhatsApp</option>
                <option value="phone">Telefone</option>
                <option value="email">E-mail</option>
              </select>
            </Field>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.can_contact} onChange={(e)=>set("can_contact", e.target.checked)} className="w-5 h-5 accent-blue-400" />
              <span className="text-sm">Autoriza receber comunicações do Conexão Inefável</span>
            </label>
          </>
        )}

        {step === 2 && (
          <>
            <Field label="Igreja / Congregação">
              <input data-testid="youth-church" placeholder="Ex: Igreja A" value={form.church} onChange={(e)=>set("church", e.target.value)} className="ci-input" />
            </Field>
            <Field label="Ministério ou grupo">
              <input placeholder="Louvor, mídia, intercessão..." value={form.ministry} onChange={(e)=>set("ministry", e.target.value)} className="ci-input" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Data da primeira visita">
                <input type="date" value={form.first_visit_date?.slice(0,10) || ""} onChange={(e)=>set("first_visit_date", e.target.value)} className="ci-input" />
              </Field>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.is_first_timer} onChange={(e)=>set("is_first_timer", e.target.checked)} className="w-5 h-5 accent-blue-400" />
                  <span className="text-sm">Visitante de primeira vez</span>
                </label>
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            {isMinor && (
              <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 p-4 space-y-4">
                <div className="label">Consentimento (menor de 18)</div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.parent_consent} onChange={(e)=>set("parent_consent", e.target.checked)} className="w-5 h-5 accent-blue-400" />
                  <span className="text-sm">Responsável autorizou a participação e o cadastro</span>
                </label>
                <Field label="Nome do responsável">
                  <input value={form.responsible_name} onChange={(e)=>set("responsible_name", e.target.value)} className="ci-input" />
                </Field>
                <Field label="Contato do responsável">
                  <input value={form.responsible_contact} onChange={(e)=>set("responsible_contact", e.target.value)} className="ci-input" />
                </Field>
              </div>
            )}
            <Field label="Observações de acompanhamento">
              <textarea rows={4} value={form.notes} onChange={(e)=>set("notes", e.target.value)} className="ci-input resize-none" placeholder="Pontos de oração, interesses, próximos passos..." />
            </Field>
            <p className="text-xs text-slate-500">Nenhum dado sensível (saúde, religião específica detalhada, etc.) deve ser registrado neste campo.</p>
          </>
        )}

        {step === 4 && (
          <div className="space-y-3 text-sm">
            <div className="label">Revisão</div>
            {[
              ["Nome", form.full_name],
              ["Nascimento", form.birth_date ? `${form.birth_date.slice(0,10)} (${age} anos)` : "—"],
              ["Cidade/UF", [form.city, form.state].filter(Boolean).join(" / ") || "—"],
              ["WhatsApp", form.whatsapp || "—"],
              ["E-mail", form.email || "—"],
              ["Igreja", form.church || "—"],
              ["Ministério", form.ministry || "—"],
              ["1ª visita", form.first_visit_date?.slice(0,10) || "—"],
              ["Visitante 1ª vez", form.is_first_timer ? "Sim" : "Não"],
              ["Autoriza contato", form.can_contact ? "Sim" : "Não"],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                <span className="text-slate-400">{l}</span>
                <span className="text-white text-right max-w-[60%] truncate">{v || "—"}</span>
              </div>
            ))}
            <p className="text-xs text-slate-500 pt-2">Campos obrigatórios: apenas o nome completo. Os demais podem ser preenchidos depois.</p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/5">
          <button type="button" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"><ArrowLeft size={16}/> Voltar</button>
          <button type="button" onClick={save} data-testid="youth-save-btn" className="btn-ghost text-xs"><Save size={14}/> Salvar rascunho</button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="youth-next-step" className="btn-primary">Continuar <ArrowRight size={16}/></button>
          ) : (
            <button type="button" onClick={save} data-testid="youth-submit-btn" className="btn-primary"><Check size={16}/> Salvar cadastro</button>
          )}
        </div>
      </form>
    </div>
  );
}
